import type React from 'react';
import { useState } from 'react';

import { DisregardEventResponse } from '@/app/models/disregardEvents';
import { useListAllEventsQuery } from '@/app/services/disregardEventsApi';
import DataTable, { bodyCellSx } from '@/components/common/DataTable';
import { Checkbox, TableCell, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ConsiderCollaboratorTableProps {
  searchTerm: string;
  selectedCollaborators: string[];
  setSelectedCollaborators: (id: string[]) => void;
}

export default function DisregardEventsTable({
  searchTerm,
  selectedCollaborators,
  setSelectedCollaborators,
}: ConsiderCollaboratorTableProps) {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { data: collaborators } = useListAllEventsQuery({
    searchTerm,
    bringExcluded: true,
    page,
    size: rowsPerPage,
    sortKey: 'name',
    sortDirection: 'ASC',
  });
  const { t } = useTranslation(['disregardEvents', 'common']);

  const handleCheckboxChange = (id: string, checked: boolean) => {
    const newSelectedCollaborators = checked
      ? [...selectedCollaborators, id]
      : selectedCollaborators.filter((collabId) => collabId !== id);

    setSelectedCollaborators(newSelectedCollaborators);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = collaborators?.content.map((collab) => collab.code);
      setSelectedCollaborators(allIds as string[]);
    } else {
      setSelectedCollaborators([]);
    }
  };

  const columns = [
    {
      key: 'select',
      label: (
        <Checkbox
          color="secondary"
          sx={{ p: 0 }}
          indeterminate={
            selectedCollaborators.length > 0 &&
            selectedCollaborators.length < (collaborators?.content?.length as number)
          }
          checked={
            (collaborators?.content?.length as number) > 0 &&
            selectedCollaborators.length === collaborators?.content.length
          }
          onChange={handleSelectAll}
          inputProps={{ 'aria-label': 'select all collaborators' }}
        />
      ),
      width: theme.spacing(6), // Small width for checkbox column
    },
    { key: 'events', label: t('disregarded.events') },
  ];

  const renderRow = (collaborator: DisregardEventResponse) => {
    const isSelected = selectedCollaborators.includes(collaborator.code);
    return (
      <>
        <TableCell sx={{ ...bodyCellSx, padding: 'checkbox' }}>
          <Checkbox
            color="secondary"
            sx={{ p: 0 }}
            checked={isSelected}
            onChange={(e) => handleCheckboxChange(collaborator.code, e.target.checked)}
            inputProps={{ 'aria-labelledby': `collaborator-${collaborator.code}` }}
          />
        </TableCell>
        <TableCell sx={bodyCellSx} id={`collaborator-${collaborator.code}`}>
          <Typography variant="body2">
            {collaborator.code} - {collaborator.name}
          </Typography>
        </TableCell>
      </>
    );
  };

  // Use the collaborator ID as the unique key
  const getRowKey = (collaborator: DisregardEventResponse) => collaborator.code;

  const paginationConfig = {
    page,
    rowsPerPage,
    totalElements: collaborators?.totalElements || 0,
    rowsPerPageOptions: [5, 10, 25],
    onPageChange: setPage,
    onRowsPerPageChange: setRowsPerPage,
    labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
      `${from}-${to} ${t('of')} ${count}`,
    labelRowsPerPage: t('items.per.page'),
  };

  return (
    <DataTable
      columns={columns}
      data={collaborators?.content || []}
      renderRow={renderRow}
      getRowKey={getRowKey}
      emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
      pagination={paginationConfig}
      size="small"
    />
  );
}
